import * as React from "react"
import { Flex, Box, Text, Image, Grid } from "theme-ui"
import { useState } from "react"
import { Link, graphql, useStaticQuery } from "gatsby"
import blackLogo from "../images/logo_tweag_new.svg"
import downArrow from "../images/down-arrow.svg"
import { pipe } from "fp-ts/function"

export const navbarHeight = `6rem`
export const navbarHeightMobile = `5rem`

type MenuItem = LinkItem | DropdownItem

type LinkItem = {
  type: `link`
  title: string
  to: string
  isExternal?: boolean
}

type DropdownItem = { type: `dropdown`; title: string; items: LinkItem[] }

type TechnicalGroup = {
  slug: string
  title: string
}

const getMenuItems = (groups: TechnicalGroup[]): MenuItem[] => [
  {
    type: `dropdown`,
    title: `Technical groups`,
    items: [{ type: `link` as const, title: `About`, to: `/groups` }].concat(
      groups.map(group => ({
        type: `link`,
        title: group.title,
        to: group.slug,
      }))
    ),
  },
  { type: `link`, title: `Open source`, to: `/opensource` },
  {
    type: `link`,
    title: `Careers`,
    to: `https://moduscreate.com/careers`,
    isExternal: true,
  },
  { type: `link`, title: `Research`, to: `/research` },
  { type: `link`, title: `Blog`, to: `/blog` },
  { type: `link`, title: `Contact`, to: `/contact` },
  {
    type: `link`,
    title: `Consulting services`,
    to: `https://moduscreate.com`,
    isExternal: true,
  },
]

const MenuText = ({ children }) => (
  <Text
    sx={{
      color: `black`,
      textTransform: `uppercase`,
    }}
  >
    {children}
  </Text>
)

function MenuLinkItem({
  label,
  to,
  isExternal,
}: {
  label: string
  to: string
  isExternal?: boolean
}) {
  const LinkComponent: React.FC<{ children: React.ReactNode }> = ({
    children,
  }) =>
    isExternal ? <a href={to}>{children}</a> : <Link to={to}>{children}</Link>

  return (
    <LinkComponent>
      <Flex
        sx={{
          flexDirection: `column`,
          gap: `.1em`,
          height: `100%`,
          justifyContent: `center`,
          width: `fit-content`,
          ":hover": {
            ".navlink-underline": {
              width: `100%`,
            },
          },
        }}
      >
        <MenuText>{label}</MenuText>
        <Underline />
      </Flex>
    </LinkComponent>
  )
}

const Underline = () => (
  <Box
    sx={{
      height: `.12em`,
      width: `0%`,
      background: `black`,
      transition: `width .3s`,
    }}
    className="navlink-underline"
  ></Box>
)

const MenuDropdownItem: React.FC<{ title: string; items: MenuItem[] }> = ({
  title,
  items,
}) => (
  <Flex
    sx={{
      flexDirection: `column`,
      position: `relative`,
      gap: `.1em`,
      height: `100%`,
      justifyContent: `center`,
      ":hover": {
        ">.navlink-underline": {
          width: `100%`,
        },
        ".arrow-rotate": {
          transform: `rotate(180deg)`,
        },
        ".dropdown-menu": {
          visibility: `visible`,
          opacity: 1,
          transform: `translateY(0)`,
        },
      },
    }}
  >
    <Flex
      sx={{ gap: `0.25rem`, alignItems: `center`, justifyContent: `center` }}
    >
      <MenuText>{title}</MenuText>
      <Flex
        sx={{
          height: `100%`,
          // In the SVG, the center of rotation is the point of the arrow.
          // So we need to move the arrow to the bottom of the container to rotate it around the center.
          alignItems: `end`,
          transition: `transform .3s`,
        }}
        className="arrow-rotate"
      >
        <Image
          sx={{
            width: `1rem`,
            height: `1rem`,
          }}
          src={downArrow}
          alt="Dropdown arrow"
        />
      </Flex>
    </Flex>
    <Flex
      sx={{
        // To prevent opening the dropdown when hovering this transparent element
        visibility: `hidden`,
        position: `absolute`,
        top: `3rem`,
        flexDirection: `column`,
        gap: `.5em`,
        // To align the dropdown with the menu item
        left: `-.5rem`,
        width: `100%`,
        // Padding to prevent the mouse from leaving the dropdown and closing it accidentally
        paddingTop: `.5rem`,
        paddingBottom: `1rem`,
        paddingX: `.5rem`,
        backgroundColor: `white`,
        opacity: 0,
        transform: `translateY(-1rem)`,
        transition: `all .3s`,
      }}
      className="dropdown-menu"
    >
      {items.map((item, i) => (
        <MenuItem isMobile={false} item={item} key={i} />
      ))}
    </Flex>
    <Underline />
  </Flex>
)

const MenuDropdownItemMobile: React.FC<{
  title: string
  items: MenuItem[]
}> = ({ title, items }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Flex
      sx={{
        flexDirection: `column`,
        gap: `.1em`,
        height: `100%`,
        justifyContent: `center`,
      }}
      onClick={() => setIsOpen(!isOpen)}
    >
      <Flex
        sx={{
          gap: `0.5rem`,
          // To align the title in the center
          // This value is the sum of the width of the arrow and the gap between the title and the arrow
          marginLeft: `2rem`,
          justifyContent: `center`,
        }}
      >
        <MenuText>{title}</MenuText>
        <Flex
          sx={{
            height: `100%`,
            alignItems: `center`,
            transform: isOpen ? `rotate(180deg)` : null,
            transition: `transform .3s`,
          }}
        >
          <Image
            sx={{
              // In the SVG, the center of rotation is the point of the arrow.
              // So we need to move the arrow to the bottom of the container to rotate it around the center.
              transform: `translateY(.175rem)`,
              width: `1.5rem`,
              height: `1.5rem`,
              objectFit: `cover`,
            }}
            src={downArrow}
            alt="Dropdown arrow"
          />
        </Flex>
      </Flex>
      <Grid
        sx={{
          // Trick to make the dropdown smoothly appear and disappear
          gridTemplateRows: isOpen ? `1fr` : `0fr`,
          gap: 0,
          paddingTop: isOpen ? `1rem` : 0,
          transition: `grid-template-rows .3s`,
        }}
      >
        <Flex
          sx={{
            flexDirection: `column`,
            gap: `1rem`,
            alignItems: `center`,
            textAlign: `center`,
            overflow: `hidden`,
            height: `100%`,
          }}
        >
          {items.map((item, i) => (
            <MenuItem isMobile={true} item={item} key={i} />
          ))}
        </Flex>
        <Box
          sx={{
            height: isOpen ? `.12em` : 0,
            width: isOpen ? `60%` : `0%`,
            margin: isOpen ? `2em auto 0 auto` : 0,
            background: `black`,
            transition: `width .3s`,
          }}
        ></Box>
      </Grid>
    </Flex>
  )
}

const MenuDesktop: React.FC<{ items: MenuItem[] }> = ({ items }) => {
  return (
    <Flex
      sx={{
        display: [`none`, `none`, `flex`],
        flexShrink: 0,
        gap: `2em`,
        height: `100%`,
      }}
    >
      <MenuItems isMobile={false} items={items} />
    </Flex>
  )
}

const MenuItems: React.FC<{ items: MenuItem[]; isMobile: boolean }> = ({
  items,
  isMobile,
}) => (
  <>
    {items.map((item, i) => (
      <MenuItem item={item} key={i} isMobile={isMobile} />
    ))}
  </>
)

const MenuItem: React.FC<{ item: MenuItem; isMobile: boolean }> = ({
  item,
  isMobile,
}) => {
  switch (item.type) {
    case `link`:
      return (
        <MenuLinkItem
          label={item.title}
          to={item.to}
          isExternal={item.isExternal}
        />
      )
    case `dropdown`:
      return isMobile ? (
        <MenuDropdownItemMobile title={item.title} items={item.items} />
      ) : (
        <MenuDropdownItem title={item.title} items={item.items} />
      )
  }
}

const MenuMobile: React.FC<{
  items: MenuItem[]
  isMobileDropdownOpened: boolean
}> = ({ items, isMobileDropdownOpened }) => {
  return (
    <Box
      sx={{
        display: [`block`, `block`, `none`],
        opacity: isMobileDropdownOpened ? 1 : 0,
        // Prevent the hidden menu from intercepting clicks
        pointerEvents: isMobileDropdownOpened ? `auto` : `none`,
        width: `100vw`,
        position: `absolute`,
        overflow: `hidden`,
        top: navbarHeightMobile,
        left: 0,
        transition: `all .3s ease`,
        zIndex: 4,
      }}
    >
      <Flex
        sx={{
          flexDirection: `column`,
          background: `white`,
          gap: `2em`,
          alignItems: `center`,
          paddingY: `2em`,
          fontSize: `1.3em`,
        }}
      >
        <MenuItems isMobile={true} items={items} />
      </Flex>
      {/* This empty element displays fades the dropdown menu from white to the background */}
      <Box
        sx={{
          height: `5rem`,
          background: `linear-gradient(0deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)`,
        }}
      ></Box>
    </Box>
  )
}

const DropdownToggle = ({ onclick, opened }) => (
  <Box
    sx={{
      display: [`block`, `block`, `none`],
      width: `2.4rem`,
      height: `2.4rem`,
      cursor: `pointer`,
      ".topbottombar": {
        opacity: opened ? 0 : 1,
      },
      ".middlebar": {
        transformOrigin: `center`,
        transition: `transform .2s`,
      },
      ".middlebar2": {
        transform: opened ? `rotate(-45deg)` : null,
      },
      ".middlebar1": {
        transform: opened ? `rotate(45deg)` : null,
      },
    }}
    onClick={onclick}
  >
    <svg viewBox="0 0 100 100" stroke="currentcolor" strokeWidth=".3em">
      <path className="topbottombar" d="M 10,29 90,29" />
      <path className="middlebar middlebar1" d="M 10,50 90,50" />
      <path className="middlebar middlebar2" d="M 10,50 90,50" />
      <path className="topbottombar" d="M 10,71 90,71" />
    </svg>
  </Box>
)

const edgesToGroups = (
  edges: Array<{
    node: { fields: { slug: string }; frontmatter: { title: string } }
  }>
): TechnicalGroup[] =>
  edges.map(edge => ({
    slug: edge.node.fields.slug,
    title: edge.node.frontmatter.title,
  }))

export const Navigation = () => {
  const [isMobileDropdownOpened, setIsMobileDropdownOpened] = useState(false)

  const {
    allMarkdownRemark: { edges },
  } = useStaticQuery(graphql`
    query {
      allMarkdownRemark(filter: { frontmatter: { key: { eq: "group" } } }) {
        edges {
          node {
            fields {
              slug
            }
            frontmatter {
              title
            }
          }
        }
      }
    }
  `)

  const menuItems = React.useMemo(
    () => pipe(edges, edgesToGroups, getMenuItems),
    [edges]
  )

  function handleToggleDropdown() {
    setIsMobileDropdownOpened(!isMobileDropdownOpened)
  }

  return (
    <Box
      sx={{
        width: `100%`,
        height: [navbarHeightMobile, navbarHeightMobile, navbarHeight],
        paddingX: `2rem`,
        background: `white`,
        zIndex: 999,
        position: `sticky`,
        top: 0,
        left: 0,
      }}
    >
      <Flex
        sx={{
          maxWidth: `90rem`,
          marginX: `auto`,
          flexDirection: `column`,
          justifyContent: `center`,
        }}
      >
        <Flex
          sx={{
            flexGrow: 1,
            paddingY: `1em`,
            height: [navbarHeightMobile, navbarHeightMobile, navbarHeight],
            gap: `3rem`,
            alignItems: `center`,
            justifyContent: `space-between`,
          }}
        >
          {/* Logo */}
          <Link to="/" sx={{ minWidth: `8rem`, flexShrink: 1 }}>
            <Image sx={{ width: `13rem` }} src={blackLogo} alt="Tweag" />
          </Link>
          {/* Menu bar when there is enough space */}
          <MenuDesktop items={menuItems} />
          {/* Dropdown menu toggle button in mobile mode */}
          <DropdownToggle
            onclick={handleToggleDropdown}
            opened={isMobileDropdownOpened}
          />
        </Flex>
      </Flex>
      <MenuMobile
        items={menuItems}
        isMobileDropdownOpened={isMobileDropdownOpened}
      />
    </Box>
  )
}
